import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Select,
  Flex,
  useToast,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

import OptionsService from "./OptionsOsService";
import {
  buscarTodasUnidadesDisponiveis,
  buscarOrdemServicoPorId,
  atualizarOrdemServico,
} from "./ServiceOrder.js";

export default function EditarOS() {
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  const [osNumber, setOsNumber] = useState("");
  const [companyAddressId, setCompanyAddressId] = useState("");
  const [dateRequest, setDateRequest] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [dateStart, setDateStart] = useState("");
  const [dateExpected, setDateExpected] = useState("");
  const [priorityId, setPriorityId] = useState("");
  const [requesterName, setRequesterName] = useState("");
  const [serviceTypeId, setServiceTypeId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [details, setDetails] = useState("");
  const [summary, setSummary] = useState("");
  const [requestClientName, setRequestClientName] = useState("");
  const [requestClientPhone, setRequestClientPhone] = useState("");
  const [requestClientMail, setRequestClientMail] = useState("");
  const [recipientClientName, setRecipientClientName] = useState("");
  const [recipientClientPhone, setRecipientClientPhone] = useState("");
  const [recipientClientMail, setRecipientClientMail] = useState("");
  const [situationId, setSituationId] = useState("");
  const [andar, setAndar] = useState("");
  const [complemento1, setComplemento1] = useState("");
  const [complemento2, setComplemento2] = useState("");

  const [priorityOptions, setPriorityOptions] = useState([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
  const [teamTypeOptions, setTeamTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [companyAddresses, setCompanyAddresses] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingOSData, setLoadingOSData] = useState(true);

  const fetchOptions = useCallback(async () => {
    try {
      const [priorityOpts, serviceTypeOpts, teamTypeOpts, statusOpts] =
        await Promise.all([
          OptionsService.getOptionsByType("priority"),
          OptionsService.getOptionsByType("typeofservice"),
          OptionsService.getOptionsByType("team"),
          OptionsService.getOptionsByType("statusofservice"),
        ]);
      setPriorityOptions(priorityOpts);
      setServiceTypeOptions(serviceTypeOpts);
      setTeamTypeOptions(teamTypeOpts);
      setStatusOptions(statusOpts);
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
      toast({
        title: "Erro.",
        description: "Não foi possível carregar as opções de dropdown.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  const fetchCompanyAddresses = useCallback(async () => {
    try {
      const addresses = await buscarTodasUnidadesDisponiveis();
      setCompanyAddresses(addresses);
    } catch (error) {
      console.error("Erro ao carregar endereços de empresas:", error);
      toast({
        title: "Erro.",
        description: "Não foi possível carregar as unidades de empresa.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  useEffect(() => {
    fetchOptions();
    fetchCompanyAddresses();
  }, [fetchOptions, fetchCompanyAddresses]);

  useEffect(() => {
    const loadOSData = async () => {
      if (!id) {
        toast({
          title: "ID da Ordem de Serviço não encontrado.",
          description: "Redirecionando para a página inicial.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        navigate("/home");
        return;
      }

      setLoadingOSData(true);
      try {
        const response = await buscarOrdemServicoPorId(id);
        const osData =
          response.content && response.content.length > 0
            ? response.content[0]
            : null;

        if (!osData) {
          toast({
            title: "Ordem de Serviço não encontrada.",
            description: "Nenhum dado retornado para o ID fornecido.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          navigate("/home");
          return;
        }

        setOsNumber(osData.orderNumber || "");
        setCompanyAddressId(String(osData.companyAddressId || ""));
        setDateRequest(
          osData.dateRequest
            ? new Date(osData.dateRequest).toISOString().split("T")[0]
            : ""
        );
        setDateEnd(
          osData.dateEnd
            ? new Date(osData.dateEnd).toISOString().split("T")[0]
            : ""
        );
        setDateStart(
          osData.dateStart
            ? new Date(osData.dateStart).toISOString().split("T")[0]
            : ""
        );
        setDateExpected(
          osData.dateExpected
            ? new Date(osData.dateExpected).toISOString().split("T")[0]
            : ""
        );
        setPriorityId(osData.priorityId || "");
        setRequesterName(osData.requesterName || "");
        setServiceTypeId(String(osData.companyRequests?.optionsId || ""));
        setTeamId(osData.teamId || "");
        setSituationId(osData.situation || "");
        setDetails(osData.details || "");
        setSummary(osData.summary || "");
        setRequestClientName(osData.requestClientName || "");
        setRequestClientPhone(osData.requestClientPhone || "");
        setRequestClientMail(osData.requestClientMail || "");
        setRecipientClientName(osData.recipientClientName || "");
        setRecipientClientPhone(osData.recipientClientPhone || "");
        setRecipientClientMail(osData.recipientClientMail || "");
        setAndar(osData.floor || ""); // Mapeia 'floor' do backend para 'andar' do frontend
        setComplemento1(osData.addressDetails || ""); // Mapeia 'addressDetails' para 'complemento1'
        setComplemento2(osData.addressDetails2 || "");
        // toast({
        //   title: "Ordem de Serviço carregada com sucesso!",
        //   status: "success",
        //   duration: 2000,
        //   isClosable: true,
        // });
      } catch (error) {
        console.error("Erro ao carregar dados da OS para edição:", error);
        toast({
          title: "Erro ao carregar OS.",
          description:
            "Não foi possível carregar os dados da Ordem de Serviço para edição. " +
            (error.message || ""),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        navigate("/home");
      } finally {
        setLoadingOSData(false);
      }
    };

    loadOSData();
  }, [id, toast, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const selectedServiceType = serviceTypeOptions.find(
      (option) => option.optionsId === serviceTypeId
    );

    const dadosOS = {
      orderNumber: osNumber,
      companyAddressId: companyAddressId,
      dateRequest: dateRequest ? `${dateRequest}T00:00:00.000Z` : null,
      dateEnd: dateEnd ? `${dateEnd}T00:00:00.000Z` : null,
      dateStart: dateStart ? `${dateStart}T00:00:00.000Z` : null,
      dateExpected: dateExpected ? `${dateExpected}T00:00:00.000Z` : null,
      priorityId: priorityId,
      requesterName: requesterName,
      companyRequests: selectedServiceType
        ? {
            optionsId: selectedServiceType.optionsId,
            description: selectedServiceType.description,
            type: selectedServiceType.type || "typeofservice",
          }
        : null,
      teamId: teamId,
      situation: situationId,
      details: details,
      summary: summary,
      requestClientName: requestClientName,
      requestClientPhone: requestClientPhone,
      requestClientMail: requestClientMail,
      recipientClientName: recipientClientName,
      recipientClientPhone: recipientClientPhone,
      recipientClientMail: recipientClientMail,
      floor: andar,
      addressDetails: complemento1,
      addressDetails2: complemento2,
    };

    try {
      await atualizarOrdemServico(id, dadosOS);
      toast({
        title: "OS atualizada com sucesso!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/home");
    } catch (error) {
      console.error("Erro ao atualizar OS:", error);
      toast({
        title: "Erro ao atualizar OS.",
        description: error.message || "Verifique os dados e tente novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingOSData) {
    return (
      <Flex justify="center" align="center" minH="100vh">
        <Spinner size="xl" color="#C1272D" />
        <Text ml={4}>Carregando Ordem de Serviço para edição...</Text>
      </Flex>
    );
  }

  return (
    <Box
      p={8}
      maxWidth="800px"
      margin="auto"
      bg="white"
      borderRadius="lg"
      boxShadow="lg"
    >
      <Heading as="h2" size="xl" mb={6} textAlign="center" color="#C1272D">
        Editar Ordem de Serviço
      </Heading>

      <form onSubmit={handleSubmit}>
        <Flex direction="column" gap={4}>
          <FormControl isRequired>
            <FormLabel>Número da OS</FormLabel>
            <Input
              type="text"
              placeholder="Ex: 12345"
              value={osNumber}
              onChange={(e) => setOsNumber(e.target.value)}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Unidade / Endereço da Empresa</FormLabel>
            <Select
              placeholder="Selecione uma Unidade"
              value={companyAddressId}
              onChange={(e) => setCompanyAddressId(e.target.value)}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            >
              {companyAddresses.map((address) => (
                <option
                  key={String(address.companyAddressId)}
                  value={String(address.companyAddressId)}
                >
                  {address.unityName}
                </option>
              ))}
            </Select>
          </FormControl>

          <Flex gap={4}>
            <FormControl isRequired>
              <FormLabel>Data da Requisição</FormLabel>
              <Input
                type="date"
                value={dateRequest}
                onChange={(e) => setDateRequest(e.target.value)}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Data de Início</FormLabel>
              <Input
                type="date"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              />
            </FormControl>
          </Flex>
          <Flex gap={4}>
            <FormControl>
              <FormLabel>Data Final</FormLabel>
              <Input
                type="date"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Data Esperada</FormLabel>
              <Input
                type="date"
                value={dateExpected}
                onChange={(e) => setDateExpected(e.target.value)}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              />
            </FormControl>
          </Flex>

          <FormControl isRequired>
            <FormLabel>Prioridade</FormLabel>
            <Select
              placeholder="Selecione a Prioridade"
              value={priorityId}
              onChange={(e) => setPriorityId(e.target.value)}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            >
              {priorityOptions.map((option) => (
                <option key={option.optionsId} value={option.optionsId}>
                  {option.description}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Nome do Solicitante</FormLabel>
            <Input
              type="text"
              placeholder="Nome completo do solicitante"
              value={requesterName}
              onChange={(e) => setRequesterName(e.target.value)}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Tipo de Serviço</FormLabel>
            <Select
              placeholder="Selecione o Tipo de Serviço"
              value={serviceTypeId}
              onChange={(e) => setServiceTypeId(e.target.value)}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            >
              {serviceTypeOptions.map((option) => (
                <option key={option.optionsId} value={option.optionsId}>
                  {option.description}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl mt={4}>
            <FormLabel>Andar</FormLabel>
            <Input
              value={andar}
              onChange={(e) => setAndar(e.target.value)}
              placeholder="Andar da localização"
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            />
          </FormControl>

          <FormControl mt={4}>
            <FormLabel>Complemento 1</FormLabel>
            <Input
              value={complemento1}
              onChange={(e) => setComplemento1(e.target.value)}
              placeholder="Complemento principal"
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            />
          </FormControl>

          <FormControl mt={4}>
            <FormLabel>Complemento 2</FormLabel>
            <Input
              value={complemento2}
              onChange={(e) => setComplemento2(e.target.value)}
              placeholder="Segundo complemento"
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Tipo de Equipe</FormLabel>
            <Select
              placeholder="Selecione o Tipo de Equipe"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            >
              {teamTypeOptions.map((option) => (
                <option key={option.optionsId} value={option.optionsId}>
                  {option.description}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Status da OS</FormLabel>
            <Select
              placeholder="Selecione o Status"
              value={situationId}
              onChange={(e) => setSituationId(e.target.value)}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            >
              {statusOptions.map((option) => (
                <option key={option.optionsId} value={option.optionsId}>
                  {option.description}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Resumo</FormLabel>
            <Input
              type="text"
              placeholder="Breve resumo da solicitação"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Detalhes</FormLabel>
            <Textarea
              placeholder="Detalhes completos da solicitação"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            />
          </FormControl>

          <Text fontSize="lg" fontWeight="bold" mt={4} mb={2} color="#C1272D">
            Dados do Solicitante Cliente
          </Text>
          <Flex gap={4}>
            <FormControl>
              <FormLabel>Nome Cliente</FormLabel>
              <Input
                type="text"
                placeholder="Nome do cliente"
                value={requestClientName}
                onChange={(e) => setRequestClientName(e.target.value)}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Telefone Cliente</FormLabel>
              <Input
                type="tel"
                placeholder="Telefone do cliente"
                value={requestClientPhone}
                onChange={(e) => setRequestClientPhone(e.target.value)}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Email Cliente</FormLabel>
              <Input
                type="email"
                placeholder="Email do cliente"
                value={requestClientMail}
                onChange={(e) => setRequestClientMail(e.target.value)}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              />
            </FormControl>
          </Flex>

          <Text fontSize="lg" fontWeight="bold" mt={4} mb={2} color="#C1272D">
            Dados do Cliente Recebedor
          </Text>
          <Flex gap={4}>
            <FormControl>
              <FormLabel>Nome Recebedor</FormLabel>
              <Input
                type="text"
                placeholder="Nome do recebedor"
                value={recipientClientName}
                onChange={(e) => setRecipientClientName(e.target.value)}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Telefone Recebedor</FormLabel>
              <Input
                type="tel"
                placeholder="Telefone do recebedor"
                value={recipientClientPhone}
                onChange={(e) => setRecipientClientPhone(e.target.value)}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Email Recebedor</FormLabel>
              <Input
                type="email"
                placeholder="Email do recebedor"
                value={recipientClientMail}
                onChange={(e) => setRecipientClientMail(e.target.value)}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              />
            </FormControl>
          </Flex>

          <Button
            type="submit"
            bg="#C1272D"
            color="white"
            _hover={{ bg: "#a81f23" }}
            size="lg"
            width="full"
            isLoading={isLoading}
            loadingText="Atualizando..."
            mt={8}
          >
            Atualizar Ordem de Serviço
          </Button>
        </Flex>
      </form>
    </Box>
  );
}
